import * as React from "react";

import { Eye, EyeOff } from "lucide-react";
import type { Control, FieldValues, Path } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/ui/form";
import { Input } from "@repo/ui/components/ui/input";
import { cn } from "@repo/ui/lib/utils";

type IconAdornmentProps = {
  icon?: React.ReactNode;
  isPassword: boolean;
  isFocused: boolean;
  isSearch: boolean;
  hasValue: boolean;
  onIconClick?: () => void;
};

function IconAdornment({
  icon,
  isPassword,
  isFocused,
  isSearch,
  hasValue,
  onIconClick,
}: Readonly<IconAdornmentProps>) {
  // Hide icon for password inputs or when type="search" has a value
  if (!icon || isPassword || (isSearch && hasValue)) return null;

  if (onIconClick) {
    return (
      <button
        type="button"
        onClick={onIconClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onIconClick();
          }
        }}
        className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer bg-transparent border-0 p-0"
        aria-label="Clear input"
        tabIndex={0}
      >
        {React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
              className: cn(
                "h-4 w-4 text-brand-black-50",
                isFocused && "text-brand-blue",
                (icon.props as { className?: string } | undefined)?.className,
              ),
            })
          : icon}
      </button>
    );
  }

  return (
    <div className="absolute top-1/2 right-3 -translate-y-1/2">
      {React.isValidElement(icon)
        ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
            className: cn(
              "h-4 w-4 text-gray-400",
              isFocused && "text-brand-blue",
              (icon.props as { className?: string } | undefined)?.className,
            ),
          })
        : icon}
    </div>
  );
}

type PasswordToggleProps = {
  isPassword: boolean;
  showPassword: boolean;
  onToggle: () => void;
  tabIndex?: number;
};

function PasswordToggle({
  isPassword,
  showPassword,
  onToggle,
  tabIndex,
}: Readonly<PasswordToggleProps>) {
  if (!isPassword) return null;
  return (
    <>
      <style>{`input[type="password"]::-ms-reveal { display: none !important; }`}</style>
      <button
        type="button"
        onClick={onToggle}
        className="hover:text-brand-blue absolute top-1/2 right-3 -translate-y-1/2 text-gray-400"
        tabIndex={tabIndex}
        aria-label={showPassword ? "Hide password" : "Show password"}
        aria-pressed={showPassword}
      >
        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </>
  );
}

export interface InputFieldProps<T extends FieldValues = FieldValues> extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  size?: "sm" | "default" | "lg";
  icon?: React.ReactNode;
  showPasswordToggle?: boolean;
  error?: string;
  success?: string;
  label?: string;
  description?: string;
  // Label presentation variant: 'external' (original) or 'inline' (chip inside input)
  labelVariant?: "external" | "inline";
  // Optional RHF integration
  control?: Control<T>;
  name?: Path<T>;
  // Custom keyup callback (matches FormFields/InputField API)
  onKeyup?: (val?: string) => void;
  // Extra class names applied to the input (align with FormFields/InputField API)
  inputClassName?: string;
  numericonly?: boolean;
  lettersonly?: boolean;
  onIconClick?: () => void;
  showError?: boolean;
  testId?: string;
}

function InputFieldInner<T extends FieldValues = FieldValues>(
  {
    size = "default",
    className,
    icon,
    showPasswordToggle = false,
    error,
    success,
    label,
    description,
    labelVariant = "external",
    type = "text",
    control,
    name,
    // Extract custom props so they are NOT forwarded to the DOM
    onKeyup,
    inputClassName,
    id,
    numericonly,
    lettersonly,
    onIconClick,
    showError = true,
    testId,
    ...restProps
  }: InputFieldProps<T>,
  ref: React.Ref<HTMLInputElement>,
) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [localValue, setLocalValue] = React.useState<
    string | number | readonly string[] | undefined
  >(restProps.value ?? restProps.defaultValue);

  const isPassword = showPasswordToggle && type === "password";
  let inputType = type;
  if (isPassword) {
    inputType = showPassword ? "text" : "password";
  }

  const isSearch = type === "search";

  // Determine disabled state from props (RHF field doesn't provide disabled)
  const isDisabled = Boolean((restProps as { disabled?: boolean }).disabled);

  // Size classes reused in both modes
  let sizeClasses: string;
  if (size === "sm") {
    sizeClasses = "h-8 px-3 text-sm";
  } else if (size === "lg") {
    sizeClasses = "h-12 px-4 text-base";
  } else {
    sizeClasses = "h-10 px-3 text-sm";
  }

  // Add extra top padding when label chip is shown so text doesn't overlap the chip
  let extraTopPadding = "";
  if (label && labelVariant === "inline") {
    if (size === "sm") extraTopPadding = "pt-4";
    else if (size === "lg") extraTopPadding = "pt-5";
    else extraTopPadding = "pt-4";
  }

  // Align label left edge to match input padding
  const labelLeftClass = size === "lg" ? "left-[14px]" : "left-[9.5px]";
  // Align label vertical offset: more space for large size
  const labelTopClass = size === "lg" ? "top-2" : "top-1";

  return (
    <div className="space-y-2" data-testid={testId}>
      {/* RHF mode */}
      {control && name ? (
        <FormField<T>
          control={control}
          name={name}
          render={({ field, fieldState }) => (
            <FormItem>
              {label && labelVariant === "external" && (
                <FormLabel className={cn(isDisabled && "opacity-50")}>{label}</FormLabel>
              )}
              <FormControl>
                <div className="relative">
                  {/* Inline label chip */}
                  {label && labelVariant === "inline" && (
                    <span
                      className={cn(
                        "absolute z-10",
                        labelTopClass,
                        labelLeftClass,
                        "text-brand-blue pointer-events-none bg-white px-1 text-[11px] leading-none font-semibold",
                        isDisabled && "opacity-50",
                      )}
                    >
                      {label}
                    </span>
                  )}
                  <Input
                    type={inputType}
                    className={cn(
                      "rounded-md border bg-white transition-all duration-200",
                      "placeholder:text-brand-black-60",
                      // Light blue focus glow
                      "focus-visible:ring-[3px] focus-visible:ring-[#BFDEFF]",
                      sizeClasses,
                      extraTopPadding,
                      fieldState.error
                        ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                        : "border-[var(--brand-black-60)] hover:border-[var(--brand-black-60)] focus-visible:border-[var(--brand-blue-20)]",
                      inputClassName,
                      className,
                    )}
                    {...field}
                    id={id ?? name}
                    data-testid={
                      testId ? `${testId}-input` : `${String(id ?? name).toLowerCase()}-input`
                    }
                    aria-label={labelVariant === "inline" ? label : undefined}
                    {...restProps}
                    onFocus={() => setIsFocused(true)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      field.onBlur();
                      setIsFocused(false);
                      (
                        restProps as {
                          onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
                        }
                      ).onBlur?.(e);
                    }}
                    onChange={(e) => {
                      let value = e.target.value;

                      if (numericonly) {
                        value = value.replace(/\D/g, "");
                      } else if (lettersonly) {
                        value = value.replace(/[^\p{L}\s'-]/gu, "");
                      }

                      field?.onChange(value);
                      (
                        restProps as { onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void }
                      ).onChange?.({ ...e, target: { ...e.target, value } });
                    }}
                    aria-invalid={!!fieldState.error}
                    aria-describedby={description ? `${name}-description` : undefined}
                    onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      onKeyup?.((e.target as HTMLInputElement).value)
                    }
                  />

                  {/* Icon */}
                  <IconAdornment
                    icon={icon}
                    isPassword={isPassword}
                    isFocused={isFocused}
                    isSearch={isSearch}
                    hasValue={Boolean(field.value !== undefined && String(field.value).length)}
                    onIconClick={onIconClick}
                  />

                  {/* Password Toggle */}
                  <PasswordToggle
                    isPassword={isPassword}
                    showPassword={showPassword}
                    onToggle={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  />
                </div>
              </FormControl>
              {description && (
                <FormDescription id={`${name}-description`}>{description}</FormDescription>
              )}
              {showError && <FormMessage />}
            </FormItem>
          )}
        />
      ) : (
        // Standalone mode (backward compatible)
        <div className="flex flex-col gap-1">
          {label && labelVariant === "external" && (
            <label
              className={cn("text-sm font-medium text-gray-700", isDisabled && "opacity-50")}
              htmlFor={id}
            >
              {label}
            </label>
          )}
          <div className="relative">
            {/* Inline label chip */}
            {label && labelVariant === "inline" && (
              <span
                className={cn(
                  "absolute z-10",
                  labelTopClass,
                  labelLeftClass,
                  "text-brand-blue pointer-events-none bg-white px-1 text-[11px] leading-none font-semibold",
                  isDisabled && "opacity-50",
                )}
              >
                {label}
              </span>
            )}
            <Input
              ref={ref}
              type={inputType}
              className={cn(
                // Base style – keep border width constant so it doesn't "jump" on error/hover
                "rounded-md border-[1.5px] bg-white font-normal transition-all duration-200",
                "placeholder:text-brand-black-60",
                // Light blue focus glow (overridden in error/success states)
                "focus-visible:ring-[3px] focus-visible:ring-[#BFDEFF]",
                sizeClasses,
                extraTopPadding,
                // Error state – only change color, not width
                error &&
                  "border-[#CF4D54] focus-visible:border-[#CF4D54] focus-visible:ring-[#CF4D54]",
                // Success state
                success &&
                  "border-green-500 focus-visible:border-green-500 focus-visible:ring-green-500",
                // Default (no error/success)
                !error &&
                  !success &&
                  "border-[var(--brand-black-60)] hover:border-[var(--brand-black-60)] focus-visible:border-[var(--brand-blue-20)]",
                inputClassName,
                className,
              )}
              id={id}
              data-testid={testId ? `${testId}-input` : `${String(id ?? name).toLowerCase()}-input`}
              aria-label={labelVariant === "inline" ? label : undefined}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              {...restProps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                (
                  restProps as {
                    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
                  }
                ).onChange?.(e);
                // Track local value so we can hide the search icon when there's content
                setLocalValue((e.target as HTMLInputElement).value);
              }}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
                onKeyup?.((e.target as HTMLInputElement).value)
              }
            />

            {/* Icon */}
            <IconAdornment
              icon={icon}
              isPassword={isPassword}
              isFocused={isFocused}
              isSearch={isSearch}
              hasValue={Boolean(
                (restProps.value ?? localValue) !== undefined &&
                String(restProps.value ?? localValue).length,
              )}
              onIconClick={onIconClick}
            />

            {/* Password Toggle */}
            <PasswordToggle
              isPassword={isPassword}
              showPassword={showPassword}
              onToggle={() => setShowPassword(!showPassword)}
            />
          </div>

          {/* Description */}
          {description && <p className="text-xs text-gray-500">{description}</p>}

          {/* Error Message */}
          {error && <p className="text-xs text-red-500">{error}</p>}

          {/* Success Message */}
          {success && <p className="text-xs text-green-500">{success}</p>}
        </div>
      )}
    </div>
  );
}

export const InputField = React.forwardRef(InputFieldInner) as <
  T extends FieldValues = FieldValues,
>(
  props: InputFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement;

(InputField as unknown as { displayName?: string }).displayName = "InputField";
